import { useState } from 'react'
import type { DictionaryIngredient } from '@entities/calculator'
import {
  MAX_INPUT_VALUE,
  parseInput,
  quantShort,
  toDecimalString,
} from '@entities/calculator'
import { useOverlayBack } from './overlayBack'

interface ProductAmountFormProps {
  ingredient: DictionaryIngredient
  /** Значения открытой позиции: объём покупки и её цена. */
  initialAmount?: string
  initialPrice?: string
  isSaving: boolean
  error?: string | null
  onSubmit: (amount: string, price: string) => void
  onCancel: () => void
}

/**
 * Объём покупки и её цена. Цену за единицу не спрашиваем: человек видит на
 * ценнике «литр за 90 ₽», а не «0,09 ₽ за мл» — делить в уме он не должен.
 */
const ProductAmountForm = ({
  ingredient,
  initialAmount = '',
  initialPrice = '',
  isSaving,
  error,
  onSubmit,
  onCancel,
}: ProductAmountFormProps) => {
  const [amount, setAmount] = useState(initialAmount)
  const [price, setPrice] = useState(initialPrice)

  useOverlayBack(onCancel)

  const amountValue = parseInput(amount)
  const priceValue = parseInput(price)

  // Бэк требует объём строго больше нуля и цену не меньше нуля. Проверяем то,
  // что реально уедет: 0,0004 после округления до трёх знаков станет нулём, и
  // бэк ответил бы 422 на значение, которое мы сами сочли верным.
  const amountToSend =
    amountValue === null ? null : Number(toDecimalString(amountValue, 3))
  const priceToSend =
    priceValue === null ? null : Number(toDecimalString(priceValue, 2))
  const isValid =
    amountToSend !== null &&
    amountToSend > 0 &&
    amountToSend <= MAX_INPUT_VALUE &&
    priceToSend !== null &&
    priceToSend >= 0 &&
    priceToSend <= MAX_INPUT_VALUE

  const unit = quantShort(ingredient.quant)
  const perUnit =
    isValid && amountToSend !== null && priceToSend !== null && amountToSend > 0
      ? priceToSend / amountToSend
      : null

  return (
    <form
      className="cost-product-form"
      onSubmit={event => {
        event.preventDefault()
        if (
          !isValid ||
          isSaving ||
          amountToSend === null ||
          priceToSend === null
        )
          return
        onSubmit(
          toDecimalString(amountToSend, 3),
          toDecimalString(priceToSend, 2)
        )
      }}
    >
      <p className="cost-product-form__name">{ingredient.name}</p>

      {/* Фокус ни одному полю не ставим: клавиатура выезжала бы сразу и
          закрывала шторку вместе с подсказкой про цену за единицу. */}
      <div className="cost-product-form__fields">
        <label>
          <span>Объём покупки, {unit}</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={event => setAmount(event.target.value)}
            placeholder={ingredient.quant === 'pcs' ? '10' : '1000'}
          />
        </label>

        <label>
          <span>Цена покупки, ₽</span>
          <input
            type="text"
            inputMode="decimal"
            value={price}
            onChange={event => setPrice(event.target.value)}
            placeholder="90"
          />
        </label>
      </div>

      {perUnit !== null && (
        <p className="cost-product-form__hint">
          Выходит{' '}
          {perUnit.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ₽ за 1{' '}
          {unit}
        </p>
      )}

      {error && <p className="cost-note warning">{error}</p>}

      <div className="cost-product-form__actions">
        <button type="button" className="cost-button ghost" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="submit"
          className="cost-button"
          disabled={!isValid || isSaving}
        >
          {isSaving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </form>
  )
}

export default ProductAmountForm
